import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * SelectInput Component (Web Implementation)
 *
 * A dropdown select component with theme integration and accessibility.
 */
import { forwardRef } from 'react';
import { useTheme } from '../../theme';
export const SelectInput = forwardRef(({ variant = 'outlined', size = 'md', label, error, helperText, hasError = false, required = false, fullWidth = false, placeholder, options, disabled, className, id, 'aria-describedby': ariaDescribedBy, 'data-testid': testId, ...props }, ref) => {
    const theme = useTheme();
    // Generate IDs for accessibility
    const selectId = id || `select-input-${Math.random().toString(36).substr(2, 9)}`;
    const errorId = error ? `${selectId}-error` : undefined;
    const helperId = helperText ? `${selectId}-helper` : undefined;
    const describedBy = [ariaDescribedBy, errorId, helperId].filter(Boolean).join(' ') || undefined;
    const isError = hasError || !!error;
    const getContainerStyles = () => {
        return {
            display: 'inline-flex',
            flexDirection: 'column',
            gap: theme.spacing.xs,
            width: fullWidth ? '100%' : 'auto',
        };
    };
    const getSelectStyles = () => {
        const baseStyles = {
            width: '100%',
            fontFamily: theme.typography.fontFamily,
            color: theme.colors.text,
            backgroundColor: variant === 'filled' ? theme.colors.surface : theme.colors.background,
            borderRadius: variant === 'underlined' ? '0' : theme.borderRadius.md,
            transition: 'all 0.2s ease',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
            appearance: 'none',
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: `right ${theme.spacing.sm} center`,
        };
        const sizeStyles = {
            sm: {
                padding: `${theme.spacing.xs} ${theme.spacing.lg} ${theme.spacing.xs} ${theme.spacing.sm}`,
                fontSize: theme.typography.fontSize.small,
            },
            md: {
                padding: `${theme.spacing.sm} ${theme.spacing.xl} ${theme.spacing.sm} ${theme.spacing.md}`,
                fontSize: theme.typography.fontSize.body,
            },
            lg: {
                padding: `${theme.spacing.md} ${theme.spacing['2xl']} ${theme.spacing.md} ${theme.spacing.lg}`,
                fontSize: theme.typography.fontSize.h6,
            },
        };
        const variantStyles = {
            outlined: {
                border: `1px solid ${isError ? theme.colors.error : theme.colors.border}`,
            },
            filled: {
                border: `1px solid ${isError ? theme.colors.error : 'transparent'}`,
            },
            underlined: {
                border: 'none',
                borderBottom: `2px solid ${isError ? theme.colors.error : theme.colors.border}`,
            },
        };
        return {
            ...baseStyles,
            ...sizeStyles[size],
            ...variantStyles[variant],
        };
    };
    const getLabelStyles = () => {
        return {
            fontSize: theme.typography.fontSize.small,
            fontWeight: theme.typography.fontWeight.medium,
            color: isError ? theme.colors.error : theme.colors.text,
            marginBottom: theme.spacing.xs,
        };
    };
    const getHelperTextStyles = () => {
        return {
            fontSize: theme.typography.fontSize.caption,
            color: isError ? theme.colors.error : theme.colors.textMuted,
            marginTop: theme.spacing.xs,
        };
    };
    return (_jsxs("div", { style: getContainerStyles(), className: className, "data-testid": testId, children: [label && (_jsxs("label", { htmlFor: selectId, style: getLabelStyles(), children: [label, required && (_jsx("span", { "aria-label": "required", style: { color: theme.colors.error, marginLeft: '4px' }, children: "*" }))] })), _jsxs("select", { ref: ref, id: selectId, disabled: disabled, required: required, "aria-invalid": isError, "aria-describedby": describedBy, "aria-required": required, style: getSelectStyles(), ...props, children: [placeholder && (_jsx("option", { value: "", disabled: true, children: placeholder })), options.map((option) => (_jsx("option", { value: option.value, disabled: option.disabled, children: option.label }, option.value)))] }), error && (_jsx("div", { id: errorId, role: "alert", style: getHelperTextStyles(), children: error })), helperText && !error && (_jsx("div", { id: helperId, style: getHelperTextStyles(), children: helperText }))] }));
});
SelectInput.displayName = 'SelectInput';
